<script lang="ts">
  import { productsStore } from "../store";
  import { fade, scale } from "svelte/transition";

  let currentPage = 1;
  let itemsPerPage = 10;

  // Sort reverse to show recent ones on top
  $: reversedProducts = [...$productsStore].reverse();
  $: totalPages = Math.ceil(reversedProducts.length / itemsPerPage);
  $: paginatedProducts = reversedProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  function changePage(page: number) {
    if (page >= 1 && page <= totalPages) {
      currentPage = page;
    }
  }

  let isModalOpen = false;
  let isEditMode = false;
  let showSuccessMsg = false;
  let formData = { name: "", description: "", price: 0, image: "" };

  function openForm(product: any = null, e?: Event) {
    if (e) e.preventDefault();
    isEditMode = !!product;
    if (product) {
      formData = { ...product };
    } else {
      formData = { name: "", description: "", price: 0, image: "" };
    }
    isModalOpen = true;
  }

  function handleFormSubmit(e: Event) {
    e.preventDefault();
    isModalOpen = false;
    showSuccessMsg = true;
    setTimeout(() => {
      showSuccessMsg = false;
    }, 3000);
  }

  function handleDelete(e: Event) {
    e.preventDefault();
    showSuccessMsg = true;
    setTimeout(() => {
      showSuccessMsg = false;
    }, 3000);
  }
</script>

<div class="w-full p-6 relative">
  {#if showSuccessMsg}
    <div
      class="fixed top-20 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl z-50 flex items-center transition-opacity"
      transition:fade
    >
      <i class="fa-solid fa-check-circle text-3xl mr-4"></i>
      <div>
        <p class="text-xl font-bold">Success!</p>
        <p class="text-base mt-1">Action completed successfully.</p>
      </div>
      <button
        class="ml-6 text-2xl hover:text-black transition"
        on:click={() => (showSuccessMsg = false)}
      >
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
  {/if}

  {#if isModalOpen}
    <div
      class="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4"
    >
      <div
        class="bg-white rounded-2xl w-full max-w-lg p-8 shadow-2xl"
        transition:scale
      >
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-3xl font-bold uppercase">
            {isEditMode ? "Edit Product" : "Add New Product"}
          </h3>
          <button
            class="text-2xl hover:text-red-500 transition"
            on:click={() => (isModalOpen = false)}
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <form on:submit={handleFormSubmit} class="flex flex-col gap-5">
          <div>
            <label
              for="product_name"
              class="block text-sm font-bold text-gray-700 mb-2"
              >Product Name</label
            >
            <input
              id="product_name"
              type="text"
              bind:value={formData.name}
              required
              class="w-full p-3 border-2 rounded-lg hover:border-sky-400 focus:border-sky-500 outline-none transition"
              placeholder="Enter product name"
            />
          </div>
          <div>
            <label
              for="product_price"
              class="block text-sm font-bold text-gray-700 mb-2"
              >Price ($)</label
            >
            <input
              id="product_price"
              type="number"
              step="0.01"
              bind:value={formData.price}
              required
              class="w-full p-3 border-2 rounded-lg hover:border-sky-400 focus:border-sky-500 outline-none transition"
              placeholder="29.99"
            />
          </div>
          <div>
            <label
              for="product_desc"
              class="block text-sm font-bold text-gray-700 mb-2"
              >Description</label
            >
            <textarea
              id="product_desc"
              bind:value={formData.description}
              required
              class="w-full p-3 border-2 rounded-lg hover:border-sky-400 focus:border-sky-500 outline-none transition resize-none"
              rows="3"
              placeholder="Enter vivid description"
            ></textarea>
          </div>
          <div class="mt-4 flex gap-4">
            <button
              type="button"
              class="flex-1 bg-gray-200 text-black font-bold py-3 rounded-lg hover:bg-gray-300 transition"
              on:click={() => (isModalOpen = false)}>Cancel</button
            >
            <button
              type="submit"
              class="flex-1 bg-sky-500 text-white font-bold py-3 rounded-lg hover:bg-amber-400 hover:text-black shadow-lg transition duration-300 text-lg uppercase tracking-wide"
              >Submit</button
            >
          </div>
        </form>
      </div>
    </div>
  {/if}

  <div
    class="flex justify-between items-center mb-6 max-sm:flex-col max-sm:items-start max-sm:gap-4"
  >
    <h2
      class="text-5xl font-black uppercase tracking-wider text-gray-800 drop-shadow-sm"
    >
      Products Management
    </h2>
    <button
      on:click={(e) => openForm(null, e)}
      class="bg-sky-500 text-white font-bold py-3 px-6 rounded-lg transition duration-400 hover:scale-105 hover:bg-amber-400 hover:text-black hover:shadow-xl max-sm:w-full max-sm:text-center text-lg"
    >
      <i class="fa-solid fa-plus mr-2"></i> Add New Product
    </button>
  </div>

  <hr class="h-px mb-8 bg-gray-300 border-0" />

  <div
    class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6"
  >
    {#each paginatedProducts as product}
      <div
        class="bg-white rounded-xl shadow-md border-2 border-transparent hover:border-sky-400 hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col relative group"
      >
        <img
          src={product.image}
          alt={product.name}
          class="w-full h-40 object-cover border-b"
        />
        <div class="p-4 flex-1 flex flex-col">
          <h3 class="font-bold text-lg mb-1 line-clamp-1">{product.name}</h3>
          <p class="text-sm text-gray-600 line-clamp-2 mb-3 flex-1">
            {product.description}
          </p>
          <div class="font-bold text-sky-600 text-xl">
            ${product.price.toFixed(2)}
          </div>
        </div>

        <div
          class="absolute inset-0 bg-sky-900/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-6"
        >
          <button
            on:click={(e) => openForm(product, e)}
            class="w-14 h-14 bg-white text-sky-600 rounded-full hover:bg-amber-400 hover:text-black shadow-2xl hover:scale-110 transition duration-300 flex items-center justify-center text-2xl"
            title="Edit"
          >
            <i class="fa-solid fa-pen"></i>
          </button>
          <button
            on:click={handleDelete}
            class="w-14 h-14 bg-white text-red-500 rounded-full hover:bg-red-500 hover:text-white shadow-2xl hover:scale-110 transition duration-300 flex items-center justify-center text-2xl"
            title="Delete"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    {/each}
  </div>

  <div class="flex justify-center mt-10 gap-2 flex-wrap">
    <button
      class="px-4 py-2 bg-sky-500 hover:bg-amber-400 hover:text-black transition text-white rounded-lg disabled:opacity-50 disabled:hover:bg-sky-500 disabled:hover:text-white"
      disabled={currentPage === 1}
      on:click={() => changePage(currentPage - 1)}>Prev</button
    >
    {#each Array(totalPages) as _, i}
      <button
        class="px-4 py-2 rounded-lg transition duration-200 {currentPage ===
        i + 1
          ? 'bg-amber-400 text-black font-bold shadow-lg scale-110'
          : 'bg-gray-200 text-gray-700 hover:bg-sky-200'}"
        on:click={() => changePage(i + 1)}>{i + 1}</button
      >
    {/each}
    <button
      class="px-4 py-2 bg-sky-500 hover:bg-amber-400 hover:text-black transition text-white rounded-lg disabled:opacity-50 disabled:hover:bg-sky-500 disabled:hover:text-white"
      disabled={currentPage === totalPages}
      on:click={() => changePage(currentPage + 1)}>Next</button
    >
  </div>
</div>
